import { computed, ref } from 'vue'

/**
 * Reusable pagination state for Pinia stores.
 *
 * Usage in a store:
 *   const { page, pageSize, hasMore, offset, setHasMore } = usePagination()
 *
 *   async function nextPage() {
 *     if (!hasMore.value) return
 *     page.value++
 *     await loadData()
 *   }
 */
export function usePagination(options: { pageSize?: number } = {}) {
  const page = ref(0)
  const pageSize = ref(options.pageSize ?? 50)
  const hasMore = ref(true)

  const offset = computed(() => page.value * pageSize.value)

  function setHasMore(total: number) {
    hasMore.value = total > (page.value + 1) * pageSize.value
  }

  /**
   * For APIs that don't return total count.
   * Assumes there's more data if the current page came back full.
   */
  function setHasMoreFromLength(dataLength: number) {
    hasMore.value = dataLength === pageSize.value
  }

  return {
    page,
    pageSize,
    hasMore,
    offset,
    setHasMore,
    setHasMoreFromLength,
  }
}
